import { createSlice } from '@reduxjs/toolkit'
import api from 'api/axios/foka'
import { identity, isNullOrUndefined } from 'utils'
import { createAxiosThunk } from 'utils/reduxUtil'
import { BizChannelTypes } from '../../Codes'

const initialState = {
  loadingError: null,
  isLoading: false,
  page: 0,
  size: 20,
  total: 0,
  channels: [],
  selectedIds: [],
}

export const fetchChannels = createAxiosThunk(
  'channelList/fetchChannels',
  (noArgs, { getState }) => {
    const { page, size } = getState().channel.channelList
    return api.channel.getChannels({
      page,
      size,
    })
  }
)

export const deleteSelectedChannels = createAxiosThunk(
  'channelList/deleteSelectedChannels',
  (noArgs, { getState }) => {
    const {
      channel: {
        channelList: { selectedIds },
      },
      account: {
        current: { id: adAccountId },
      },
    } = getState()
    return api.channel.deleteChannels({
      bizChannelIds: selectedIds,
      adAccountId,
    })
  }
)

export const unmapTalkChannel = createAxiosThunk(
  'channelList/unmapTalkChannel',
  ({ bizChannelId, talkChannelId }) =>
    api.channel.unmapTalkChannel({
      bizChannelId,
      talkChannelId,
    })
)

export const registerPayBadge = createAxiosThunk(
  'channelList/registerPayBadge',
  ({ bizChannelId }) => api.channel.registerPayBadge(bizChannelId)
)

const channelListSlice = createSlice({
  name: 'channelList',
  initialState,

  reducers: {
    reset: () => initialState,
    setPage: (state, { payload }) => {
      state.page = payload
    },
    setSize: (state, { payload }) => {
      state.size = payload
    },
    setSelectedIds: (state, { payload }) => {
      state.selectedIds = payload
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchChannels.pending, state => {
      state.loadingError = null
      state.isLoading = true
    })

    builder.addCase(fetchChannels.fulfilled, (state, { payload }) => {
      state.isLoading = false
      state.total = payload.totalElements
      state.channels = payload.content.map(
        ({
          id,
          name,
          siteUrl,
          reviewStatus,
          pausedReason,
          bizChannelCategory,
          talkChannel,
          talkChannelAccounts,
          bizChannelType,
        }) => ({
          id,
          name,
          reviewStatus,
          pausedReason,
          talkChannel,
          talkChannelAccounts,
          bizChannelType,
          urls: [
            siteUrl.mobileSiteYn === 'Y' &&
              `${siteUrl.mobileUrl.protocol.toLowerCase()}://${
                siteUrl.mobileUrl.url
              }`,
            siteUrl.pcSiteYn === 'Y' &&
              `${siteUrl.pcUrl.protocol.toLowerCase()}://${siteUrl.pcUrl.url}`,
          ].filter(identity),
          isPaused: !isNullOrUndefined(pausedReason),
          isDisabled:
            bizChannelType === BizChannelTypes.TALK_CHANNEL &&
            talkChannel.enabled !== 'Y',
          categoryFullPath: bizChannelCategory.categoryFullPath,
        })
      )
    })

    builder.addCase(fetchChannels.rejected, (state, { error }) => {
      state.loadingError = error || true
      state.isLoading = false
    })

    builder.addCase(deleteSelectedChannels.pending, state => {
      state.isLoading = true
    })

    builder.addCase(deleteSelectedChannels.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(deleteSelectedChannels.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(unmapTalkChannel.pending, state => {
      state.isLoading = true
    })

    builder.addCase(unmapTalkChannel.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(unmapTalkChannel.rejected, state => {
      state.isLoading = false
    })
  },
})

export const { reset, setPage, setSize, setSelectedIds } =
  channelListSlice.actions

export default channelListSlice.reducer
